import React, { useContext, useEffect, useState } from 'react';
import { PRISMAX_BACKEND_URL } from '../../../configs/config';
import { SwapMainAppContext } from '../../../context/SwapMainAppContext';
import { getApiErrorMessage, isSessionExpiredError } from '../../../utils/authErrors';
import styles from '../RoboticsDataBeta/RoboticsDataBetaApplications.module.css';

export default function SubscribersInformation({ adminToken }) {
    const { handleAdminSessionExpired } = useContext(SwapMainAppContext);
    const [page, setPage] = useState(1);
    const [revision, setRevision] = useState(0);
    const [result, setResult] = useState({ data: [], total: 0, total_pages: 1 });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!adminToken) return;
        const controller = new AbortController();
        setLoading(true);
        setError('');
        const load = async () => {
            try {
                const response = await fetch(
                    `${PRISMAX_BACKEND_URL}/api/admin/robotics-data-subscribers?page=${page}`,
                    { headers: { Authorization: `Bearer ${adminToken}` }, signal: controller.signal },
                );
                const body = await response.json();
                if (!response.ok || !body.success) {
                    const message = getApiErrorMessage(body, 'Failed to load subscribers.');
                    if (isSessionExpiredError(message)) handleAdminSessionExpired?.();
                    throw new Error(message);
                }
                if (!controller.signal.aborted) setResult(body);
            } catch (loadError) {
                if (!controller.signal.aborted) setError(loadError.message || 'Failed to load subscribers.');
            } finally {
                if (!controller.signal.aborted) setLoading(false);
            }
        };
        void load();
        return () => controller.abort();
    }, [adminToken, page, revision, handleAdminSessionExpired]);

    return (
        <section className={styles.page}>
            <div className={styles.header}>
                <div>
                    <p className={styles.eyebrow}>ROBOTICS DATA</p>
                    <h1>Subscribers’ Information</h1>
                    <p>Users with a non-empty download membership. Membership values are shown as stored.</p>
                </div>
                <button type="button" className={styles.refreshButton} disabled={loading}
                    onClick={() => setRevision((value) => value + 1)}>Refresh</button>
            </div>
            {error ? <div role="alert" className={styles.error}>{error}</div> : null}
            {loading ? <div className={styles.state}>Loading subscribers…</div> : null}
            {!loading && !error && !result.data.length ? <div className={styles.state}>No subscribers found.</div> : null}
            {!loading && !error && result.data.length > 0 ? (
                <div className={styles.tableWrap}>
                    <table className={styles.table}>
                        <thead><tr><th>User</th><th>Download membership</th></tr></thead>
                        <tbody>{result.data.map((user) => (
                            <tr key={user.userid}>
                                <td>
                                    <span>{user.email?.trim() || user.solana_receive_address || '—'}</span>
                                    <span className={styles.secondary}>ID: {user.userid}</span>
                                </td>
                                <td>{user.user_data_download_membership}</td>
                            </tr>
                        ))}</tbody>
                    </table>
                </div>
            ) : null}
            <nav aria-label="Subscribers pagination" className={styles.pagination}>
                <button type="button" className={styles.refreshButton} disabled={loading || page <= 1}
                    onClick={() => setPage((value) => value - 1)}>Previous</button>
                <span>Page {page} of {result.total_pages} · {result.total} subscribers</span>
                <button type="button" className={styles.refreshButton} disabled={loading || !!error || page >= result.total_pages}
                    onClick={() => setPage((value) => value + 1)}>Next</button>
            </nav>
        </section>
    );
}
